/* sb-fit.js  (Matt, 2026-09-15): no word ever breaks mid-word.
   Text shrinks to fit instead, and the shrink is uniform per tier so no
   single word is smaller than its siblings:
     display  = every H1/H2 on the page scales together (4:2 ratio kept),
                floor 0.7 so an H2 never closes in on body size;
     nav      = phone header nav + utility words scale together;
     ui       = each buy-box form's pills/selects scale together.
   Body copy never scales (it wraps at spaces and is the readability floor).
   Second pass: a banner whose text box is taller than its image frame pulls
   the display tier down further; if it still cannot fit at the floor, the
   frame grows to the text (min-height) so nothing bleeds outside it.
   One CSS var per tier; the tokens in sb-type.css / header.liquid / sb-jpdp.css
   consume them. Nothing here is page-specific. */
(() => {
  const GROUPS = [
    { root: '#MainContent', items: 'h1, h2, h3, .heading, .banner__title, .sb-jpdp-name, .sb-jpdp-name__line, .rte h1, .rte h2, .rte h3', prop: '--sb-fit', floor: 0.66, banners: true },
    { root: '.header', items: '.sb-mobile-navigation > *, .header__menu .menu__item, .header__icons--end .sb-util-word', prop: '--sb-fit-nav', floor: 0.7 },
    { root: '.sb-jpdp-buybox__form', items: ':not([data-sb-choose]) > * > .label-swatch, :not([data-sb-choose]) .sb-jpdp-grind-pill, .sb-jpdp-grind:not([data-sb-choose]) .sb-jpdp-grind-select', prop: '--sb-fit-ui', floor: 0.65 },
    // the bundle "how we choose" row carries long labels; it fits on its own so it never drags the bag and grind pills down
    { root: '[data-sb-choose]', items: '.label-swatch, .sb-jpdp-grind-select', prop: '--sb-fit-choose', floor: 0.6 }
    // Matt 2026-09-16: cards NO LONGER scale. Card text is one fixed size and the
    // product grid drops a product per row instead of shrinking type (sb-card.css
    // DECISION 3). The --sb-fit-card group is removed so no card text is ever shrunk.
  ];
  const ctx = document.createElement('canvas').getContext('2d');
  const SPLIT = /\s+/; // hyphenated compounds count as one word: "micro-/batch" reads as a mid-word break

  function visible(el) {
    if (!el.offsetWidth && !el.offsetHeight) return false;
    const cs = getComputedStyle(el);
    // visibility:hidden still has layout (off-screen carousel slides): measure it,
    // so the fit is right the moment it scrolls into view
    if (cs.display === 'none') return false;
    if (cs.position === 'absolute' && parseFloat(cs.width) <= 1) return false; // sr-only
    return true;
  }

  function longestWordPx(el, cs) {
    // a <select> only shows its selected option; hidden options must not drive the fit
    let text;
    if (el.tagName === 'SELECT') text = el.options[el.selectedIndex] ? el.options[el.selectedIndex].text : '';
    else {
      // join text nodes with spaces so adjacent spans ("Panama" + "Gold Label") are not read as one word
      const parts = []; const tw = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      while (tw.nextNode()) parts.push(tw.currentNode.textContent);
      text = parts.join(' ');
    }
    text = text.replace(/\s+/g, ' ').trim();
    if (!text) return 0;
    ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    const ls = parseFloat(cs.letterSpacing) || 0;
    let worst = 0;
    // a select cannot wrap, so its whole visible text is the unit
    for (const w of (el.tagName === 'SELECT' ? [text] : text.split(SPLIT))) {
      if (!w) continue;
      const px = ctx.measureText(w).width + ls * w.length;
      if (px > worst) worst = px;
    }
    return worst;
  }

  function contentWidth(box) {
    const cs = getComputedStyle(box);
    return box.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
  }
  function availPx(el, cs) {
    let box = el;
    // inline elements report the line box, so measure the nearest block parent
    if (cs.display.startsWith('inline') && el.parentElement) box = el.parentElement;
    // an element sized by its own content (min-content) is wider than the space it
    // sits in; the real bound is the narrowest ancestor within a few levels
    let avail = contentWidth(box); let p = box.parentElement;
    for (let i = 0; i < 4 && p && p !== document.body; i++) {
      const w = contentWidth(p);
      if (w > 0 && w < avail) avail = w;
      if (p.id === 'MainContent') break;
      p = p.parentElement;
    }
    return avail;
  }

  function fitGroup(root, g) {
    const current = parseFloat(getComputedStyle(root).getPropertyValue(g.prop)) || 1;
    let need = 1;
    root.querySelectorAll(g.items).forEach((el) => {
      if (!visible(el)) return;
      const cs = getComputedStyle(el);
      const avail = availPx(el, cs);
      if (avail <= 0) return;
      const word = longestWordPx(el, cs) / current; // width at scale 1
      if (word > avail * 1.01 + 0.5) { need = Math.min(need, avail / word); if (window.sbFitLog) window.sbFitLog.push([g.prop, el.tagName, (el.className || '').toString().slice(0, 30), Math.round(word), Math.round(avail)]); }
    });
    let fit = Math.max(g.floor, Math.min(1, need * 0.995));
    root.style.setProperty(g.prop, fit.toFixed(3));

    if (!g.banners) return;
    // banner boxes must sit inside their frame
    root.querySelectorAll('.banner').forEach((bn) => { bn.style.minHeight = ''; });
    for (let pass = 0; pass < 3; pass++) {
      let worst = 1;
      root.querySelectorAll('.banner').forEach((bn) => {
        const box = bn.querySelector('.banner__box');
        if (!box || !visible(box)) return;
        const frame = bn.clientHeight, need = box.scrollHeight;
        if (need > frame + 1) worst = Math.min(worst, frame / need);
      });
      if (worst >= 1) break;
      const next = Math.max(g.floor, fit * worst * 0.98);
      if (next >= fit - 0.001) break;
      fit = next;
      root.style.setProperty(g.prop, fit.toFixed(3));
    }
    root.querySelectorAll('.banner').forEach((bn) => {
      const box = bn.querySelector('.banner__box');
      if (!box || !visible(box)) return;
      if (box.scrollHeight > bn.clientHeight + 1) bn.style.minHeight = box.scrollHeight + 'px';
    });
  }

  let t;
  const queue = () => { clearTimeout(t); t = setTimeout(run, 60); };
  // carousels and lazy layouts resize their cards after load: refit on any root resize
  const ro = window.ResizeObserver ? new ResizeObserver(queue) : null;
  function run() {
    GROUPS.forEach((g) => document.querySelectorAll(g.root).forEach((root) => {
      fitGroup(root, g);
      if (ro && !root.dataset.sbFitRo) { root.dataset.sbFitRo = '1'; ro.observe(root); }
    }));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(queue);
  window.addEventListener('resize', queue);
  // carousels reveal slides on scroll/drag: refit cheaply after scrolling settles
  let st; window.addEventListener('scroll', () => { clearTimeout(st); st = setTimeout(run, 200); }, { passive: true });
  window.addEventListener('load', queue);
  document.addEventListener('shopify:section:load', queue);
  document.addEventListener('shopify:section:reorder', queue);
  document.addEventListener('shopify:block:select', queue);
  window.sbFit = run;
})();
